import { useReducer, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarOff,
  ClipboardList,
  House,
  MessageSquare,
  RotateCw,
  Smile,
  Stethoscope,
  AlertCircle,
} from "lucide-react";
import { useSchedulerExitToStart, useSchedulerSend, useSchedulerSelector } from "../context";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { errorService } from "@ascleo/client/services/error.service";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { Tile, type TileTone } from "../../../components/Tile";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskLoadingPanel, KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { appointmentTypeDisplayName } from "./appointment-type-name";

interface AppointmentType {
  id: string;
  label: string;
  description?: string;
  duration?: number;
  kioskPolicy?: {
    mode: "enabled" | "disabledVisible";
    disabledReason?: string;
  };
}

type TypeLoadState = { readonly types: readonly AppointmentType[]; readonly isLoading: boolean; readonly error: string | null };
type TypeLoadEvent = { type: "started" } | { type: "loaded"; types: readonly AppointmentType[] } | { type: "failed"; message: string };
const initialTypeLoad: TypeLoadState = { types: [], isLoading: true, error: null };
function typeLoadReducer(state: TypeLoadState, event: TypeLoadEvent): TypeLoadState {
  if (event.type === "started") return state.isLoading ? state : { ...state, isLoading: true, error: null };
  if (event.type === "loaded") return { types: event.types, isLoading: false, error: null };
  return { types: [], isLoading: false, error: event.message };
}

/** Tile tones cycled across the (dynamic) appointment-type reason tiles. */
const REASON_TONES: TileTone[] = [
  "sky-subtle",
  "orange-subtle",
  "blue-subtle",
  "success-subtle",
];

/**
 * Icons cycled across the reason tiles (types are practice-defined, so no
 * semantic icon is known). None of them is the hub's "Termin buchen" icon (KO-60).
 */
const REASON_ICONS = [
  <Stethoscope key="stethoscope" />,
  <Smile key="smile" />,
  <ClipboardList key="clipboard" />,
  <MessageSquare key="message" />,
];

/**
 * Appointment type (reason) selection step — handoff "Anliegen" layout.
 *
 * Types are loaded from the scheduler RPC and rendered as whole-tile-clickable,
 * content-sized reason tiles (KO-15). The tile title is the type's readable
 * name, never the practice-internal short code such as "KU" (KO-60). Empty,
 * loading and error states use the shared state panels with a way home
 * (KO-11, KO-65). Back navigation is handled by the unified footer back button.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <AppointmentTypeStep />
 * ```
 */
export function AppointmentTypeStep() {
  const { t } = useTranslation();
  const send = useSchedulerSend();
  const exitToStart = useSchedulerExitToStart();
  const enabledQuestionnaire = useSchedulerSelector((s) => s.context.questionnaireEnabled);
  const route = useSchedulerSelector((s) => s.context.questionnaireRoute);
  const [{ types, isLoading, error }, dispatchLoad] = useReducer(typeLoadReducer, initialTypeLoad);

  // Play audio on mount
  useAudio("scheduler-appointmentType");

  const fetchTypes = useCallback(async () => {
    dispatchLoad({ type: "started" });
    try {
      const appointmentTypes =
        await docCirrusRpcClient.frontdesk.getAppointmentTypes({});
      const permitted = enabledQuestionnaire === true
        ? appointmentTypes.filter((type) => route !== undefined && route.kind !== "reception" && route.appointmentTypeIds.includes(type.id) && type.kioskPolicy?.mode !== "disabledVisible")
        : appointmentTypes;
      dispatchLoad({ type: "loaded", types: permitted });
    } catch (err) {
      frontdeskDiagnosticLogger.error("Error fetching appointment types", {
        error: err,
      });
      const userMessage = t("self-service.scheduler.appointment-type.error");
      dispatchLoad({ type: "failed", message: userMessage });
      errorService.report(err, {
        layer: "rpc",
        source: "frontdesk.scheduler.appointment-types.fetch",
        userMessage,
        suppressToast: true,
      });
    }
  }, [t, enabledQuestionnaire, route]);

  useEffect(
    function runFetchTypesEffect() {
      void fetchTypes();
    },
    [fetchTypes],
  );

  const handleSelect = (type: AppointmentType) => {
    send({
      type: "SELECT_APPOINTMENT_TYPE",
      appointmentTypeId: type.id,
      appointmentTypeName: appointmentTypeDisplayName(type),
    });
  };

  const enabledTypesCount = types.filter(
    (type) => type.kioskPolicy?.mode !== "disabledVisible",
  ).length;

  const title = t(route?.kind === "alternatives" ? "self-service.scheduler.questionnaire.alternatives" : "self-service.scheduler.appointment-type.title");
  const homeAction = {
    label: t("self-service.scheduler.done.back-to-start"),
    icon: House,
    onClick: exitToStart,
    testId: "appointment-type-home",
  };

  if (isLoading) {
    return (
      <KioskStep width="narrow" align="center" title={title}>
        <KioskLoadingPanel title={t("self-service.scheduler.appointment-type.loading")} />
      </KioskStep>
    );
  }

  if (error) {
    return (
      <KioskStep width="narrow" align="center" title={title}>
        <KioskStatePanel
          tone="error"
          icon={<Stethoscope />}
          title={error}
          body={t("self-service.help-reception-toast")}
          action={{
            label: t("common.retry"),
            icon: RotateCw,
            onClick: () => void fetchTypes(),
            testId: "appointment-type-retry",
          }}
          secondaryAction={homeAction}
        />
      </KioskStep>
    );
  }

  if (types.length === 0) {
    return (
      <KioskStep width="narrow" align="center" title={title}>
        <KioskStatePanel
          tone="neutral"
          testId="appointment-type-empty"
          icon={<CalendarOff />}
          title={t("self-service.scheduler.appointment-type.empty-title")}
          body={t("self-service.help-reception-toast")}
          action={homeAction}
        />
      </KioskStep>
    );
  }

  return (
    <KioskStep
      width="wide"
      title={title}
      lead={t("self-service.scheduler.appointment-type.lead")}
    >
      {enabledTypesCount === 0 ? (
        <KioskStatePanel
          tone="warning"
          testId="appointment-type-no-bookable"
          title={t("self-service.scheduler.appointment-type.none-bookable-title")}
          body={t("self-service.scheduler.appointment-type.none-bookable-body")}
          action={homeAction}
        />
      ) : null}

      <div className="grid grid-cols-1 content-start gap-4 md:grid-cols-2">
        {types.map((type, index) => {
          const isDisabled = type.kioskPolicy?.mode === "disabledVisible";
          const disabledReason =
            type.kioskPolicy?.disabledReason ??
            t("self-service.scheduler.appointment-type.disabled-default");

          return (
            <Tile
              key={type.id}
              data-testid={`appointment-type-${type.id}`}
              className="min-h-36"
              tone={
                isDisabled
                  ? "stone"
                  : REASON_TONES[index % REASON_TONES.length]
              }
              name={appointmentTypeDisplayName(type)}
              desc={isDisabled ? disabledReason : undefined}
              icon={
                isDisabled ? (
                  <AlertCircle />
                ) : (
                  REASON_ICONS[index % REASON_ICONS.length]
                )
              }
              footer={
                isDisabled
                  ? t(
                      "self-service.scheduler.appointment-type.disabled-footer",
                    )
                  : type.duration
                    ? t("self-service.scheduler.appointment-type.duration", { minutes: type.duration })
                    : undefined
              }
              disabled={isDisabled}
              aria-disabled={isDisabled}
              onClick={isDisabled ? undefined : () => handleSelect(type)}
            />
          );
        })}
      </div>
    </KioskStep>
  );
}
